// src/features/adhesives/generatePDF.ts
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { convertPolishChars } from '../../utils/pdfFonts';

export const generateAdhesivePDF = (data: any, mode: 'client' | 'production') => {
  const { input, result, adhesive, process, warnings } = data;
  
  const pdf = new jsPDF() as any;
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // Kolory
  const primaryColor = [34, 197, 94]; // green-500
  const darkBg = [39, 39, 42]; // zinc-800
  const dangerColor = [239, 68, 68]; // red-500
  const warningColor = [245, 158, 11]; // amber-500
  const infoColor = [59, 130, 246]; // blue-500
  
  // Funkcja rysowania gradientu
  const drawGradientBackground = (y: number, height: number, baseColor: number[]) => {
    const steps = 20;
    for (let i = 0; i < steps; i++) {
      const ratio = i / steps;
      const color = [
        baseColor[0] - ratio * 20,
        baseColor[1] - ratio * 20,
        baseColor[2] - ratio * 10
      ];
      pdf.setFillColor(color[0], color[1], color[2]);
      pdf.rect(0, y + (height / steps) * i, pageWidth, height / steps, 'F');
    }
  };
  
  // STRONA 1 - NAGŁÓWEK I PARAMETRY
  drawGradientBackground(0, 45, primaryColor);
  
  // Logo
  pdf.setFillColor(255, 255, 255);
  pdf.circle(20, 22, 8, 'F');
  pdf.setTextColor(34, 197, 94);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('KLEJ', 20, 25, { align: 'center' });
  
  // Tytuł
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(22);
  pdf.text(
    mode === 'client' 
      ? convertPolishChars('Karta Klejenia Krawędziowego - Klient')
      : convertPolishChars('Karta Klejenia Krawędziowego - Produkcja'),
    pageWidth / 2, 20, { align: 'center' }
  );
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'normal');
  pdf.text(convertPolishChars('Parametry techniczne i zalecenia'), pageWidth / 2, 28, { align: 'center' });
  
  // Data i numer
  pdf.setFontSize(9);
  pdf.text(`Data: ${new Date().toLocaleDateString('pl-PL')}`, pageWidth - 20, 15, { align: 'right' });
  pdf.text(`Nr: #${Date.now().toString().slice(-8)}`, pageWidth - 20, 20, { align: 'right' });
  
  let y = 55;
  
  // STATUS POŁĄCZENIA
  const isOk = result.ok && result.utilization <= 1.0;
  const statusColor = isOk ? [34, 197, 94] : [239, 68, 68];
  const statusBg = isOk ? [240, 253, 244] : [254, 242, 242];
  
  pdf.setFillColor(...statusBg);
  pdf.roundedRect(15, y, pageWidth - 30, 35, 3, 3, 'F');
  
  pdf.setFillColor(...statusColor);
  pdf.circle(30, y + 17, 5, 'F');
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.text(
    isOk 
      ? convertPolishChars('✓ Połączenie spełnia wymagania')
      : convertPolishChars('✗ Połączenie NIE spełnia wymagań'),
    45, y + 15
  );
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  const utilizationPercent = (result.utilization * 100).toFixed(0);
  const utilizationColor = result.utilization > 0.8 ? dangerColor : result.utilization > 0.5 ? warningColor : primaryColor;
  pdf.setTextColor(...utilizationColor);
  pdf.text(
    convertPolishChars(`Wykorzystanie spoiny: ${utilizationPercent}% (τ = ${result.tau_MPa.toFixed(2)} MPa z ${result.tau_allow_MPa} MPa)`),
    45, y + 23
  );
  
  y += 45;
  
  // PARAMETRY ZADANIA
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(39, 39, 42);
  pdf.text(convertPolishChars('PARAMETRY ZADANIA'), 15, y);
  y += 8;
  
  const taskData = [
    [convertPolishChars('Parametr'), convertPolishChars('Wartość')],
    [convertPolishChars('Materiały'), `${input.materials[0]} + ${input.materials[1]}`],
    [convertPolishChars('Grubość'), `${input.thickness_mm} mm`],
    [convertPolishChars('Szerokość spoiny'), `${input.bond_width_mm} mm`],
    [convertPolishChars('Długość zakładki'), `${input.overlap_length_mm} mm`],
    [convertPolishChars('Obciążenie'), `${input.load_N} N`],
    [convertPolishChars('Temperatura pracy'), `${input.temp_C} °C`],
    [convertPolishChars('Pole spoiny'), `${result.area_mm2} mm²`],
  ];
  
  pdf.autoTable({
    startY: y,
    head: [taskData[0]],
    body: taskData.slice(1),
    theme: 'grid',
    headStyles: {
      fillColor: [34, 197, 94],
      textColor: 255,
      fontSize: 10,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9,
      cellPadding: 3
    },
    alternateRowStyles: {
      fillColor: [250, 250, 251]
    },
    margin: { left: 15, right: 15 }
  });
  
  y = pdf.lastAutoTable.finalY + 15;
  
  // REKOMENDOWANY KLEJ
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text(convertPolishChars('REKOMENDOWANY KLEJ'), 15, y);
  y += 10;
  
  // Karty z informacjami o kleju
  const cardWidth = (pageWidth - 45) / 3;
  const cardHeight = 28;
  
  // Określ kolor dla typu kleju
  const classColors: { [key: string]: number[] } = {
    'UV': [147, 51, 234],
    'Solvent': [245, 158, 11],
    'Solvent-raw': [239, 68, 68],
    '2K': [59, 130, 246],
  };
  const adhesiveColor = classColors[adhesive.class] || [100, 100, 100];
  
  const cards = [
    { 
      label: 'Nazwa', 
      value: adhesive.name,
      sub: adhesive.vendor,
      color: adhesiveColor
    },
    { 
      label: 'Typ', 
      value: adhesive.class + (adhesive.subclass ? `-${adhesive.subclass}` : ''),
      sub: convertPolishChars(adhesive.gap_mm ? `Szczelina: ${adhesive.gap_mm[0]}-${adhesive.gap_mm[1]}mm` : ''),
      color: adhesiveColor
    },
    { 
      label: convertPolishChars('Lepkość'), 
      value: adhesive.viscosity_mPas ? `${adhesive.viscosity_mPas}` : '—',
      sub: 'mPa·s',
      color: [100, 100, 100]
    }
  ];
  
  cards.forEach((card, index) => {
    const x = 15 + index * (cardWidth + 3);
    
    // Tło karty
    pdf.setFillColor(250, 250, 251);
    pdf.roundedRect(x, y, cardWidth, cardHeight, 2, 2, 'F');
    
    // Kolorowy akcent
    pdf.setFillColor(...card.color);
    pdf.rect(x, y, cardWidth, 3, 'F');
    
    // Tekst
    pdf.setTextColor(100, 100, 100);
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.text(card.label, x + cardWidth/2, y + 8, { align: 'center' });
    
    pdf.setTextColor(...card.color);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    const lines = pdf.splitTextToSize(card.value, cardWidth - 4);
    pdf.text(lines[0], x + cardWidth/2, y + 16, { align: 'center' });
    
    if (card.sub) {
      pdf.setTextColor(100, 100, 100);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.text(card.sub, x + cardWidth/2, y + 23, { align: 'center' });
    }
  });
  
  y += cardHeight + 15;
  
  // SZCZEGÓŁY KLEJU
  const adhesiveDetails = [
    [convertPolishChars('Właściwość'), convertPolishChars('Wartość')],
    [convertPolishChars('Dystrybutorzy'), adhesive.distributors?.join(', ') || '—'],
    [convertPolishChars('Cena orientacyjna'), adhesive.price_PLN || '—'],
    [convertPolishChars('Czas manipulacji'), adhesive.handling_time || '—'],
    [convertPolishChars('Pełne utwardzenie'), adhesive.full_cure_time || '—'],
  ];
  
  if (adhesive.class === 'UV' && adhesive.uv) {
    adhesiveDetails.push(
      [convertPolishChars('Długość fali UV'), adhesive.uv.wavelength_nm || '—'],
      [convertPolishChars('Dawka UV'), adhesive.uv.dose_mJ_cm2 || '—'],
      [convertPolishChars('Czas naświetlania'), adhesive.uv.typical_cure_s || '—']
    );
  }
  
  // Dodaj informację o zamienniku jeśli istnieje
  if (adhesive.notes?.[0]?.includes('Zamiennik PL:')) {
    adhesiveDetails.push(
      ['', ''], // pusty wiersz
      [convertPolishChars('ZAMIENNIK PL'), adhesive.notes[0].replace('UŻYWANY. ', '')]
    );
  }
  
  pdf.autoTable({
    startY: y,
    head: [adhesiveDetails[0]],
    body: adhesiveDetails.slice(1),
    theme: 'striped',
    headStyles: {
      fillColor: [39, 39, 42],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold'
    },
    bodyStyles: {
      fontSize: 9
    },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 15, right: 15 }
  });
  
  // STRONA 2 - PROCES, BHP, OSTRZEŻENIA
  pdf.addPage();
  y = 20;
  
  // Nagłówek strony 2
  pdf.setFillColor(39, 39, 42);
  pdf.rect(0, 0, pageWidth, 30, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(
    mode === 'production' 
      ? convertPolishChars('Instrukcja Produkcyjna')
      : convertPolishChars('Wskazówki Techniczne'),
    pageWidth / 2, 20, { align: 'center' }
  );
  
  y = 40;
  
  // OSTRZEŻENIA KOMPATYBILNOŚCI
  if (warnings && warnings.length > 0) {
    pdf.setFillColor(254, 242, 242);
    pdf.roundedRect(15, y, pageWidth - 30, 10 + warnings.length * 6, 3, 3, 'F');
    
    pdf.setTextColor(239, 68, 68);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(convertPolishChars('OSTRZEŻENIA KOMPATYBILNOŚCI'), 20, y + 7);
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    let warningY = y + 14;
    warnings.forEach((warning: string) => {
      const lines = pdf.splitTextToSize(convertPolishChars(warning), pageWidth - 50);
      lines.forEach((line: string) => {
        pdf.text(`! ${line}`, 25, warningY);
        warningY += 5;
      });
    });
    y = warningY + 5;
  }
  
  // PROCES KLEJENIA
  pdf.setFillColor(239, 246, 255);
  pdf.roundedRect(15, y, pageWidth - 30, 10 + process.steps.length * 7, 3, 3, 'F');
  
  pdf.setTextColor(59, 130, 246);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text(convertPolishChars('PROCES KLEJENIA - KROK PO KROKU'), 20, y + 7);
  
  pdf.setTextColor(0, 0, 0);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  let stepY = y + 14;
  process.steps.forEach((step: string, idx: number) => {
    const lines = pdf.splitTextToSize(convertPolishChars(`${idx + 1}. ${step}`), pageWidth - 50);
    lines.forEach((line: string) => {
      pdf.text(line, 25, stepY);
      stepY += 5;
    });
    stepY += 2;
  });
  y = stepY + 5;
  
  // BHP
  if (process.bhp && process.bhp.length > 0) {
    pdf.setFillColor(255, 251, 235);
    pdf.roundedRect(15, y, pageWidth - 30, 10 + process.bhp.length * 7, 3, 3, 'F');
    
    pdf.setTextColor(245, 158, 11);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(convertPolishChars('WYMOGI BHP'), 20, y + 7);
    
    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    let bhpY = y + 14;
    process.bhp.forEach((note: string) => {
      const lines = pdf.splitTextToSize(convertPolishChars(note), pageWidth - 50);
      lines.forEach((line: string) => {
        pdf.text(convertPolishChars(`⚠ ${line}`), 25, bhpY);
        bhpY += 5;
      });
      bhpY += 2;
    });
    y = bhpY + 5;
  }
  
  // DODATKOWE INFORMACJE DLA PRODUKCJI
  if (mode === 'production') {
    // Parametry krytyczne
    pdf.setFillColor(243, 244, 246);
    pdf.roundedRect(15, y, pageWidth - 30, 50, 3, 3, 'F');
    
    pdf.setTextColor(75, 85, 99);
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'bold');
    pdf.text(convertPolishChars('PARAMETRY KRYTYCZNE'), 20, y + 7);
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(31, 41, 55);
    
    const criticalParams = [
      convertPolishChars(`Kontrola szczeliny: ${adhesive.gap_mm ? `${adhesive.gap_mm[0]}-${adhesive.gap_mm[1]} mm` : 'wg TDS'}`),
      convertPolishChars(`Temperatura pomieszczenia: ${input.temp_C} ± 5°C`),
      convertPolishChars(`Wilgotność względna: < 60%`),
      convertPolishChars(`Czystość powierzchni: odtłuszczone IPA`),
    ];
    
    if (adhesive.class === 'UV') {
      criticalParams.push(
        convertPolishChars(`Lampa UV: ${adhesive.uv?.wavelength_nm || '365-400 nm'}`),
        convertPolishChars(`Moc UV: ${adhesive.uv?.dose_mJ_cm2 || 'min. 100 mW/cm²'}`)
      );
    }
    
    let paramY = y + 14;
    criticalParams.forEach((param: string) => {
      pdf.text(`• ${param}`, 25, paramY);
      paramY += 6;
    });
  }
  
  // STOPKA
  const addFooter = (pageNum: number) => {
    pdf.setDrawColor(200, 200, 200);
    pdf.line(15, pageHeight - 20, pageWidth - 15, pageHeight - 20);
    
    pdf.setFontSize(8);
    pdf.setTextColor(128, 128, 128);
    pdf.setFont('helvetica', 'normal');
    
    // Logo i nazwa
    pdf.text('PlexiSystem CRM', 15, pageHeight - 12);
    pdf.text(convertPolishChars('© 2025 PlexiSystem'), 15, pageHeight - 8);
    
    // Data i godzina
    const now = new Date();
    pdf.text(
      `Wygenerowano: ${now.toLocaleDateString('pl-PL')} ${now.toLocaleTimeString('pl-PL')}`,
      pageWidth / 2,
      pageHeight - 12,
      { align: 'center' }
    );
    
    // Numer strony
    pdf.text(
      `Strona ${pageNum} z 2`,
      pageWidth - 15,
      pageHeight - 12,
      { align: 'right' }
    );
    
    // Kontakt
    pdf.text('www.plexisystem.pl', pageWidth - 15, pageHeight - 8, { align: 'right' });
    
    // Uwaga na dole
    if (pageNum === 2) {
      pdf.setFontSize(7);
      pdf.setTextColor(180, 180, 180);
      pdf.text(
        convertPolishChars('Uwaga: DCM tylko PMMA↔PMMA. UV wymaga okularów ochronnych. Dla PC/PET-G wykonaj próbę.'),
        pageWidth / 2,
        pageHeight - 4,
        { align: 'center' }
      );
    }
  };
  
  // Dodaj stopki
  pdf.setPage(1);
  addFooter(1);
  pdf.setPage(2);
  addFooter(2);
  
  // Zapisz PDF
  const filename = `klejenie_${input.materials[0]}_${input.materials[1]}_${adhesive.id}_${mode}_${new Date().toISOString().slice(0, 10)}.pdf`;
  pdf.save(filename);
};
